import { useEffect, useState } from "react";
import { api } from "../api";
import { getCurrentUser } from "../api";
import type { ContactLogEntry } from "../types";
import { Plus, Trash2, Edit3, MessageSquare } from "lucide-react";

export function ContactLog({ memberId }: { memberId: string }) {
  const [entries, setEntries] = useState<ContactLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split("T")[0]);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNote, setEditNote] = useState("");
  const [editDate, setEditDate] = useState("");
  const user = getCurrentUser();

  const load = async () => {
    try {
      const res = await api.getContactLog(memberId);
      setEntries(res.entries);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [memberId]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    try {
      const res = await api.addContactLog(memberId, note.trim(), entryDate);
      setEntries([res.entry, ...entries]);
      setNote("");
      setEntryDate(new Date().toISOString().split("T")[0]);
    } catch (e: any) { alert(e.message || "Failed to add entry"); }
    setSaving(false);
  };

  const handleUpdate = async (id: string) => {
    try {
      const res = await api.updateContactLog(id, editNote, editDate);
      setEntries(entries.map((e) => e.id === id ? { ...e, ...res.entry } : e));
      setEditingId(null);
    } catch (e: any) { alert(e.message || "Failed to update"); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this entry?")) return;
    try {
      await api.deleteContactLog(id);
      setEntries(entries.filter((e) => e.id !== id));
    } catch (e: any) { alert(e.message || "Failed to delete"); }
  };

  function fmtDate(d: string) {
    const parts = d.split("-");
    if (parts.length !== 3) return d;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4"><MessageSquare className="w-5 h-5" />Contact Log</h3>

      {/* Add entry form */}
      <form onSubmit={handleAdd} className="flex gap-2 mb-4">
        <input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm w-36 focus:outline-none focus:border-[#5f1b2c]" />
        <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note..." className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#5f1b2c]" disabled={saving} />
        <button type="submit" disabled={saving || !note.trim()} className="px-4 py-2 bg-[#5f1b2c] text-white rounded-lg text-sm font-medium hover:bg-[#4a1520] disabled:opacity-50 transition-colors shrink-0"><Plus className="w-4 h-4" /></button>
      </form>

      {/* Entries */}
      {loading ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-16 bg-gray-50 rounded-lg animate-pulse" />)}</div>
      ) : entries.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center">No entries yet. Add your first note above.</p>
      ) : (
        <div className="space-y-2">
          {entries.map((e) => (
            <div key={e.id} className="bg-gray-50 rounded-lg p-3">
              {editingId === e.id ? (
                <div className="space-y-2">
                  <input type="date" value={editDate} onChange={(ev) => setEditDate(ev.target.value)} className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm w-36" />
                  <input type="text" value={editNote} onChange={(ev) => setEditNote(ev.target.value)} className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm" />
                  <div className="flex gap-2">
                    <button onClick={() => handleUpdate(e.id)} className="px-3 py-1 bg-[#5f1b2c] text-white rounded text-xs font-medium">Save</button>
                    <button onClick={() => setEditingId(null)} className="px-3 py-1 border border-gray-200 rounded text-xs text-gray-500">Cancel</button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-gray-400">{fmtDate(e.entry_date)} — {e.author_name}</span>
                    {user?.id === e.admin_user_id && (
                      <div className="flex gap-1">
                        <button onClick={() => { setEditingId(e.id); setEditNote(e.note); setEditDate(e.entry_date); }} className="text-gray-400 hover:text-[#5f1b2c] p-0.5"><Edit3 className="w-3 h-3" /></button>
                        <button onClick={() => handleDelete(e.id)} className="text-gray-400 hover:text-red-500 p-0.5"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-800 mt-1">{e.note}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}